<template>
  <main class="wallet-transfer-page">
    <AppNavBar :title="t('home.transfer')" background-color="#f5f5f5" />

    <section class="wallet-card wallet-card--mode-tabs">
      <div class="transfer-mode-tabs" role="tablist">
        <button
          type="button"
          :class="{ active: recipientType === 'userId' }"
          :aria-selected="recipientType === 'userId'"
          role="tab"
          @click="recipientType = 'userId'"
        >
          {{ t("wallet.transferToUser") }}
        </button>
        <button
          type="button"
          :class="{ active: recipientType === 'address' }"
          :aria-selected="recipientType === 'address'"
          role="tab"
          @click="recipientType = 'address'"
        >
          {{ t("wallet.transferToExternalWallet") }}
        </button>
      </div>
    </section>

    <section class="wallet-card wallet-card--recipient">
      <h2>
        {{
          recipientType === "userId"
            ? t("wallet.receiveUser")
            : t("wallet.receiveAddress")
        }}
      </h2>
      <div class="recipient-input">
        <van-field
          v-model="addressTo"
          class="recipient-field"
          :type="recipientType === 'userId' ? 'digit' : 'text'"
          :maxlength="recipientType === 'userId' ? 8 : undefined"
          :placeholder="
            recipientType === 'userId'
              ? t('wallet.enterZorPayId')
              : t('wallet.enterOrPasteAddress')
          "
        />
        <button
          v-if="recipientType === 'address'"
          class="scan-btn"
          type="button"
          @click="openScanner"
        >
          <img :src="transferWalletScanIcon" alt="" />
        </button>
        <img
          v-else
          :src="transferRecipientUserIcon"
          alt=""
          class="recipient-user-icon"
        />
      </div>
      <div v-if="recipientType === 'userId'" class="recipient-hint">
        <span class="recipient-hint__icon">
          <img :src="transferUserIdHintIcon" alt="" />
        </span>
        <span>{{ t("wallet.onlySupport8DigitUserId") }}</span>
      </div>
    </section>

    <section class="wallet-card wallet-card--amount">
      <div class="amount-title">
        <h2>{{ t("wallet.transferAmount") }}</h2>
        <span class="amount-available" v-if="recipientType === 'userId'">
          {{ t("wallet.available") }}
          {{ transferAvailableBalance }}
          {{ amountCoin.symbol || amountCoin.coin || "" }}
        </span>
      </div>

      <div class="amount-field">
        <van-field
          v-model="amount"
          type="number"
          class="amount-control"
          input-align="left"
          :placeholder="t('wallet.enterAmount')"
        />
        <div class="amount-side">
          <button class="all-btn" type="button" @click="fillAll">
            {{ t("wallet.all") }}
          </button>
        </div>
      </div>

      <img :src="transferBalanceDividerIcon" alt="" class="balance-divider" />

      <div class="wallet-balance-row">
        <span>{{ t("wallet.walletBalance") }}</span>
        <div data-strong>{{ availableText }}</div>
      </div>
    </section>

    <van-button
      block
      round
      type="primary"
      class="submit-btn"
      :disabled="submitDisabled"
      @click="submitTransfer"
    >
      {{
        recipientType === "address"
          ? t("common.notAvailableYet")
          : t("common.confirm")
      }}
    </van-button>

    <PayPasswordPopup
      v-model:show="showPayPassword"
      v-model:password="payPassword"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      :show-forgot="true"
      :confirm-disabled="
        !payPassword.trim() || isSubmitting || isCheckingPassword
      "
      @confirm="confirmPassword"
      @cancel="resetPasswordPopup"
      @closed="resetPasswordPopup"
      @forgot="goForgotPassword"
    />

    <van-popup
      :show="showTransferVerification"
      position="bottom"
      class="transfer-email-verification-popup"
      :close-on-click-overlay="false"
      :overlay-style="{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }"
      @update:show="handleTransferVerificationShowUpdate"
      @closed="resetTransferVerification"
    >
      <section class="transfer-email-verification">
        <header class="transfer-email-verification__head">
          <h2>{{ t("wallet.transferIdentityVerification") }}</h2>
          <button type="button" @click="closeTransferVerification">
            <img :src="transferDialogCloseIcon" alt="" />
          </button>
        </header>

        <p class="transfer-email-verification__desc">
          {{ t("wallet.transferEmailVerificationDesc") }}
        </p>

        <div class="transfer-email-verification__account">
          <span class="transfer-email-verification__image">
            <img :src="transferEmailVerifyImage" alt="" />
          </span>
          <span class="transfer-email-verification__email">
            <strong>{{ t("wallet.transferVerifyEmail") }}</strong>
            <span>{{ transferEmail || "--" }}</span>
          </span>
        </div>

        <div class="transfer-email-verification__code">
          <label>{{ t("auth.emailCode") }}</label>
          <van-field
            v-model="emailCode"
            type="digit"
            maxlength="8"
            :placeholder="t('auth.enterVerificationCode')"
          >
            <template #button>
              <button
                type="button"
                class="transfer-email-verification__send"
                :disabled="
                  isSendingEmailCode || emailCountdown > 0 || !transferEmail
                "
                @click="sendTransferEmailCode"
              >
                {{
                  emailCountdown > 0
                    ? `${t("auth.resend")}${emailCountdown}s`
                    : t("auth.getCode")
                }}
              </button>
            </template>
          </van-field>
          <p>{{ t("wallet.transferEmailCodeExpires") }}</p>
        </div>

        <div class="transfer-email-verification__footer">
          <van-button
            block
            round
            type="primary"
            :disabled="!emailCode.trim() || isSubmitting"
            @click="verifyTransfer"
          >
            {{ t("auth.verify") }}
          </van-button>
          <span class="transfer-email-verification__home-indicator" />
        </div>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import { devicesInfo, payPwdCheck } from "@/api/auth";
import { pcCardEmailSend } from "@/api/card";
import { pcCardAssetTransfer } from "@/api/wallet";
import { useWalletScanner } from "@/composables/useWalletScanner";
import { useProfileStore } from "@/stores/profile";
import { useWalletTokenStore } from "@/stores/wallet-token";
import {
  findWithdrawCoin,
  useWalletWithdrawStore,
} from "@/stores/wallet-withdraw";
import { useWalletTransferFormStore } from "@/stores/wallet-transfer-form";
import { formatTokenAmount } from "@/utils/format";
import { getRouteState, navigateWithState } from "@/utils/route-state";
import { getDeviceId } from "@/utils/device";
import { showPassedToast } from "@/utils/toast";
import transferWalletScanIcon from "@/assets/images/SVG/icon/transfer-wallet-scan.svg";
import transferRecipientUserIcon from "@/assets/images/SVG/icon/transfer-recipient-user.svg";
import transferUserIdHintIcon from "@/assets/images/SVG/icon/transfer-user-id-hint.svg";
import transferBalanceDividerIcon from "@/assets/images/SVG/icon/transfer-balance-divider.svg";
import transferDialogCloseIcon from "@/assets/images/SVG/icon/transfer-dialog-close.svg";
import transferEmailVerifyImage from "@/assets/images/transfer-email-verify.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { openWalletScanner } = useWalletScanner();
const profileStore = useProfileStore();
const walletTokenStore = useWalletTokenStore();
const walletWithdrawStore = useWalletWithdrawStore();
const transferFormStore = useWalletTransferFormStore();
const pageState = computed(() => getRouteState(route, "wallet-transfer", {}));

const addressTo = ref("");
const recipientType = ref("userId");
const amount = ref("");
const showPayPassword = ref(false);
const payPassword = ref("");
const pendingPayPassword = ref("");
const showTransferVerification = ref(false);
const emailCode = ref("");
const transferEmail = ref(
  String(profileStore.currentAccount?.email || "").trim(),
);
const emailCountdown = ref(0);
const isSendingEmailCode = ref(false);
const isCheckingPassword = ref(false);
const isSubmitting = ref(false);
let emailCountdownTimer = null;

const selectedCoin = computed(
  () =>
    walletTokenStore.currentAsset ||
    walletTokenStore.assetList[0] || {
      symbol: "USDT",
      coin: "USDT",
      balance: "0",
      img: "",
      logo: "",
    },
);
const withdrawCoin = computed(() =>
  findWithdrawCoin(walletWithdrawStore.list, selectedCoin.value),
);
const amountCoin = computed(
  () =>
    withdrawCoin.value || {
      symbol: selectedCoin.value.symbol || selectedCoin.value.coin || "",
      balanceNormal: "0",
    },
);
const transferAvailableBalance = computed(
  () => amountCoin.value.balanceNormal || amountCoin.value.balance || "0",
);
const availableText = computed(() =>
  `${formatTokenAmount(transferAvailableBalance.value)} ${amountCoin.value.symbol || amountCoin.value.coin || ""}`.trim(),
);
const availableAmount = computed(() =>
  Number(transferAvailableBalance.value || 0),
);
const amountValue = computed(() => Number(amount.value || 0));
const submitDisabled = computed(
  () =>
    recipientType.value !== "userId" ||
    !/^\d{8}$/.test(addressTo.value.trim()) ||
    isSubmitting.value,
);

watch(recipientType, () => {
  addressTo.value = "";
});

watch(
  () => pageState.value.scanResult,
  (value) => {
    if (!value) {
      return;
    }
    addressTo.value = String(value).split("&")[0];
    navigateWithState(router, route.path, {
      namespace: "wallet-transfer",
      replace: true,
      payload: {
        ...pageState.value,
        scanResult: "",
      },
    });
  },
  { immediate: true },
);

function openScanner() {
  openWalletScanner({
    back: "/wallet/transfer",
    stateNamespace: "wallet-transfer",
    statePayload: {
      ...pageState.value,
    },
  });
}

function fillAll() {
  amount.value = String(transferAvailableBalance.value || "0");
}

async function submitTransfer() {
  if (recipientType.value === "address") {
    return;
  }

  if (!amount.value.trim()) {
    showFailToast(t("wallet.enterAmount"));
    return;
  }

  if (!Number.isFinite(amountValue.value) || amountValue.value <= 0) {
    showFailToast(t("card.enterValidAmount"));
    return;
  }

  if (!addressTo.value.trim()) {
    showFailToast(t("wallet.enterZorPayId"));
    return;
  }

  if (!/^\d{8}$/.test(addressTo.value.trim())) {
    showFailToast(t("wallet.enterZorPayId"));
    return;
  }

  if (amountValue.value > availableAmount.value) {
    showFailToast(`${t("wallet.maxAmountLabel")} ${availableText.value}`);
    return;
  }

  if (String(profileStore.currentAccount?.type || "") === "1") {
    showFailToast(t("article.observerWalletWarn"));
    return;
  }

  emailCode.value = "";
  pendingPayPassword.value = "";
  showPayPassword.value = true;
}

function resetPasswordPopup() {
  payPassword.value = "";
  showPayPassword.value = false;
  if (!showTransferVerification.value) {
    pendingPayPassword.value = "";
  }
}

async function confirmPassword() {
  if (isCheckingPassword.value) {
    return;
  }

  const payPwd = String(payPassword.value || "").trim();
  if (!payPwd) {
    showFailToast(t("auth.enterPassword"));
    return;
  }

  isCheckingPassword.value = true;
  try {
    const isValid = await payPwdCheck({ payPwd });
    if (!isValid) {
      showFailToast(t("auth.passwordError"));
      return;
    }

    pendingPayPassword.value = payPwd;
    showTransferVerification.value = true;
    showPayPassword.value = false;
    void loadTransferEmail();
  } catch (error) {
    showFailToast(error?.msg || error?.message || t("common.retryLater"));
  } finally {
    isCheckingPassword.value = false;
  }
}

async function loadTransferEmail() {
  const currentEmail = String(profileStore.currentAccount?.email || "").trim();
  if (currentEmail) {
    transferEmail.value = currentEmail;
    return currentEmail;
  }

  try {
    const result = await devicesInfo(
      { token: getDeviceId() },
      { meta: { showLoading: false } },
    );
    const loadedEmail = String(result?.email || "").trim();
    if (loadedEmail) {
      transferEmail.value = loadedEmail;
      profileStore.setCurrentAccount({
        ...profileStore.currentAccount,
        email: loadedEmail,
      });
    }
    return loadedEmail;
  } catch {
    return "";
  }
}

function startEmailCountdown() {
  emailCountdown.value = 60;
  window.clearInterval(emailCountdownTimer);
  emailCountdownTimer = window.setInterval(() => {
    emailCountdown.value -= 1;
    if (emailCountdown.value <= 0) {
      window.clearInterval(emailCountdownTimer);
      emailCountdownTimer = null;
    }
  }, 1000);
}

async function sendTransferEmailCode() {
  if (isSendingEmailCode.value || emailCountdown.value > 0) {
    return;
  }

  isSendingEmailCode.value = true;
  try {
    const email = await loadTransferEmail();
    if (!email) {
      showFailToast(t("auth.bindEmailTip"));
      return;
    }
    if (!profileStore.platformAddress) {
      showFailToast(t("common.retryLater"));
      return;
    }

    await pcCardEmailSend({
      address: profileStore.platformAddress,
      email,
    });
    startEmailCountdown();
    showPassedToast(t("auth.codeSent"));
  } catch (error) {
    showFailToast(error?.msg || error?.message || t("common.retryLater"));
  } finally {
    isSendingEmailCode.value = false;
  }
}

function closeTransferVerification() {
  showTransferVerification.value = false;
}

function handleTransferVerificationShowUpdate(value) {
  showTransferVerification.value = value;
}

function resetTransferVerification() {
  emailCode.value = "";
  pendingPayPassword.value = "";
  payPassword.value = "";
  if (emailCountdown.value <= 0) {
    transferEmail.value = String(
      profileStore.currentAccount?.email || "",
    ).trim();
  }
}

async function verifyTransfer() {
  if (isSubmitting.value) return;
  if (!pendingPayPassword.value) {
    showFailToast(t("auth.enterPassword"));
    return;
  }
  if (!emailCode.value.trim()) {
    showFailToast(t("auth.codeRequired"));
    return;
  }

  isSubmitting.value = true;
  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    await pcCardAssetTransfer(
      {
        toUserId: addressTo.value.trim(),
        symbol: String(
          selectedCoin.value.symbol || selectedCoin.value.coin || "",
        ).trim(),
        amount: amount.value.trim(),
        payPwd: pendingPayPassword.value,
        emailCode: emailCode.value.trim(),
      },
      { meta: { showLoading: false } },
    );
    closeToast();
    showTransferVerification.value = false;
    navigateWithState(router, "/wallet/transfer/success", {
      namespace: "wallet-transfer-success",
      replace: true,
      payload: {
        amount: amount.value.trim(),
        symbol: String(
          selectedCoin.value.symbol || selectedCoin.value.coin || "",
        ).trim(),
        userId: addressTo.value.trim(),
      },
    });
  } catch (error) {
    closeToast();
    showFailToast(t(error?.i18nKey || "common.retryLater"));
  } finally {
    isSubmitting.value = false;
  }
}

function goForgotPassword() {
  resetPasswordPopup();
  router.push("/auth/forgot-pay-password");
}

async function loadAssets() {
  await walletTokenStore.fetchTransferCoinList({
    meta: { showLoading: false },
  });
  if (!walletTokenStore.currentAsset && walletTokenStore.assetList.length) {
    walletTokenStore.setCurrentAsset(walletTokenStore.assetList[0]);
  }
}

async function loadWithdrawInfo() {
  await walletWithdrawStore.fetchWithdrawInfo({
    address: profileStore.platformAddress || null,
    reset: !walletWithdrawStore.list.length,
  });
}

onMounted(() => {
  const draft = transferFormStore.consumeDraft();
  if (draft) {
    addressTo.value = draft.addressTo;
    recipientType.value = draft.recipientType;
    amount.value = draft.amount;
  }
  // loadAssets();
  loadWithdrawInfo().catch((error) => {
    showFailToast(error?.msg || t("common.retryLater"));
  });
});

onBeforeUnmount(() => {
  window.clearInterval(emailCountdownTimer);
});
</script>

<style scoped lang="scss">
.wallet-transfer-page {
  min-height: var(--app-viewport-height);
  padding: 0 16px 34px;
  background: #f5f5f5;
}

.wallet-card {
  margin-top: 10px;
  padding: 20px 16px;
  border-radius: 16px;
  background: #ffffff;
}

.wallet-card--mode-tabs {
  height: 58px;
  margin-top: 12px;
  padding: 10px 16px;
  box-sizing: border-box;
}

.transfer-mode-tabs {
  display: grid;
  width: 100%;
  height: 38px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-radius: 12px;
  background: #f0f0f0;

  button {
    min-width: 0;
    padding: 0 4px;
    border: 0;
    background: transparent;
    color: #484748;
    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
    white-space: nowrap;

    &.active {
      border: 1px solid var(--wp-color-brand-1, #7442e8);
      border-radius: 12px;
      background: var(--wp-color-card-1, #f1eaff);
      color: var(--wp-color-brand-1, #7442e8);
      font-weight: 500;
    }
  }
}

.wallet-card--recipient {
  margin-top: 12px;
  padding: 20px 16px;
  box-sizing: border-box;
}

.recipient-input,
.amount-field {
  display: flex;
  align-items: center;
  height: 42px;
  padding: 0 10px;
  border-radius: 10px;
  background: #f4f5f6;
}

.recipient-input {
  margin-top: 20px;
  gap: 12px;
}

.recipient-field,
.amount-control {
  flex: 1;
  min-width: 0;
  padding: 0;
  background: transparent;
}

.recipient-input :deep(.van-cell),
.amount-field :deep(.van-cell) {
  padding: 0;
  background: transparent;
}

.recipient-input :deep(.van-field__control),
.amount-field :deep(.van-field__control) {
  height: 42px;
  color: #111111;
  font-size: 14px;
  line-height: 42px;
}

.recipient-input :deep(.van-field__control::placeholder),
.amount-field :deep(.van-field__control::placeholder) {
  color: #666666;
}

.scan-btn {
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
}

.scan-btn img,
.recipient-user-icon {
  display: block;
  width: 20px;
  height: 20px;
}

.recipient-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  color: #999999;
  font-size: 12px;
  line-height: 16px;
}

.recipient-hint__icon {
  display: inline-flex;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #999999;
  border-radius: 50%;
}

.recipient-hint__icon img {
  display: block;
  width: 1.78px;
  height: 10.67px;
}

.wallet-card h2 {
  margin: 0;
  color: #333333;
  font-size: 16px;
  font-weight: 500;
  line-height: 16px;
}

.amount-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.amount-available {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  font-weight: bold;
}

.amount-title h2 {
  color: #000000;
}

.amount-field {
  margin-top: 20px;
  gap: 12px;
}

.amount-side {
  display: flex;
  align-items: center;
  gap: 12px;
}

.all-btn {
  display: inline-flex;
  width: 50px;
  height: 28px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: var(--wp-color-brand-1, #7442e8);
  color: #ffffff;
  font-size: 12px;
}

.submit-btn {
  display: block;
  width: 100%;
  height: 44px;
  margin: 20px 0 0;
  --van-button-primary-background: var(--wp-color-brand-1, #7442e8);
  --van-button-primary-border-color: var(--wp-color-brand-1, #7442e8);
  font-size: 16px;
  font-weight: 600;
}

.submit-btn:disabled,
.submit-btn.van-button--disabled {
  border-color: #e0e0e0;
  background: #e0e0e0;
  color: #999999;
  opacity: 1;
}

.wallet-card--amount {
  height: 170px;
  margin-top: 12px;
  padding: 20px 16px;
  box-sizing: border-box;
}

.balance-divider {
  display: block;
  max-width: 100%;
  height: auto;
  margin-top: 19px;
}

.wallet-balance-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;

  span {
    color: #111111;
    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
  }

  [data-strong] {
    color: #111111;
    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
  }
}

.transfer-email-verification-popup {
  right: 0;
  left: 0;
  width: 100%;
  max-width: 375px;
  height: min(412px, 90vh);
  margin: 0 auto;
  overflow: hidden;
  border-radius: 20px 20px 0 0;
  background: #ffffff;
}

.transfer-email-verification {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  box-sizing: border-box;
  padding: 20px 16px 0;
  background: #ffffff;
}

.transfer-email-verification__head {
  position: relative;
  display: flex;
  height: 22px;
  flex: 0 0 22px;
  align-items: center;
  justify-content: center;

  h2 {
    margin: 0;
    color: #000000;
    font-size: 20px;
    font-weight: 600;
    line-height: 22px;
  }

  button {
    position: absolute;
    top: 3px;
    right: 0;
    display: flex;
    width: 16px;
    height: 16px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
  }

  img {
    display: block;
    width: 16px;
    height: 16px;
  }
}

.transfer-email-verification__desc {
  margin: 30px 0 26px;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
  line-height: 26px;
  text-align: center;
}

.transfer-email-verification__account {
  display: flex;
  height: 72px;
  flex: 0 0 72px;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f7f3ff;
}

.transfer-email-verification__image {
  position: relative;
  display: block;
  width: 52px;
  height: 42px;
  flex: 0 0 52px;
  overflow: hidden;

  img {
    position: absolute;
    top: -16.67%;
    left: 0;
    display: block;
    width: 100%;
    height: 123.81%;
    object-fit: cover;
  }
}

.transfer-email-verification__email {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 10px;

  strong {
    color: #090a1d;
    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
  }

  span {
    overflow: hidden;
    color: #7442e8;
    font-size: 14px;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.transfer-email-verification__code {
  margin-top: 16px;

  label {
    display: block;
    color: #000000;
    font-size: 14px;
    line-height: 14px;
  }

  :deep(.van-field) {
    height: 42px;
    margin-top: 12px;
    padding: 8px 8px 8px 16px;
    border-radius: 10px;
    background: #f4f5f6;
  }

  :deep(.van-cell::after) {
    display: none;
  }

  :deep(.van-field__control) {
    color: #090a1d;
    font-size: 14px;
    line-height: 14px;
  }

  :deep(.van-field__control::placeholder) {
    color: #999999;
  }

  :deep(.van-field__button) {
    padding-left: 8px;
  }

  > p {
    margin: 10px 0 0;
    color: #999999;
    font-size: 12px;
    line-height: 12px;
    text-align: right;
  }
}

.transfer-email-verification__send {
  display: inline-flex;
  min-width: 78px;
  height: 26px;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border: 0;
  border-radius: 6px;
  background: #e7e5ff;
  color: #7442e8;
  font-size: 12px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.7;
  }
}

.transfer-email-verification__footer {
  display: flex;
  width: 100%;
  flex: 0 0 80px;
  flex-direction: column;
  align-items: center;
  margin-top: auto;

  :deep(.van-button) {
    height: 46px;
    border: 0;
    background: #7442e8;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
  }

  :deep(.van-button--disabled) {
    opacity: 0.55;
  }
}

.transfer-email-verification__home-indicator {
  position: relative;
  display: block;
  width: 100%;
  height: 34px;
  flex: 0 0 34px;

  &::after {
    position: absolute;
    bottom: 8px;
    left: 50%;
    width: 135px;
    height: 5px;
    border-radius: 50px;
    background: #000000;
    content: "";
    transform: translateX(-50%);
  }
}

@media (max-height: 460px) {
  .transfer-email-verification-popup {
    height: 90vh;
    overflow-y: auto;
  }

  .transfer-email-verification {
    min-height: 412px;
  }
}
</style>
